import { useCallback } from 'react'
import { Pause, Play, Square } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, ButtonGroup, LoadingState, Tooltip } from '@highlight/ui'
import { useAudioNoteStatus } from '@renderer/hooks/audio/use-audio-note-selectors'
import { AUDIO_INDICATOR_COLOR, useAudioIndicatorTone } from '@renderer/hooks/audio/use-audio-indicator-tone'
import { useAudioElapsedTime } from '@renderer/hooks/audio/useAudioElapsedTime'
import { useLiveRecordingControls } from '@renderer/hooks/audio/use-live-recording-controls'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { AudioWaveform } from '@renderer/overlay/components/audio-waveform'
import { MeetingMicStatus } from '@renderer/overlay/components/meeting-mic-status'
import { useLiveMeetingIdentity } from '../../meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '../../meeting/meeting-view-id'
import { useViewActions } from '../../../view-atoms'

/**
 * The shell is 28px, the floating bar's button height, so the pill lines up with
 * the jump-to-latest and notepad buttons beside it. The fill is opaque because
 * the row floats over scrolling text, which would otherwise show through.
 *
 * select-none beats the global `button { select-text }` re-enable. Without it a
 * stray drag leaves a selection highlight painted behind the ticking timer.
 */
const PILL_SHELL_CLASS = 'bg-background select-none [&_*]:select-none'

/**
 * The ticking half of the pill, split out rather than inlined: the hook behind
 * it re-renders its host once a second, and only unmounting it actually stops
 * that where the timer is hidden.
 */
function AudioPillElapsed() {
  const elapsedTime = useAudioElapsedTime()
  const tone = useAudioIndicatorTone()

  return <span className={twMerge('tabular-nums', AUDIO_INDICATOR_COLOR[tone])}>{elapsedTime}</span>
}

interface SidepanelAudioPillProps {
  /**
   * Hidden where the pill shares the floating bar with another view's controls
   * (the Action View, Figma 8004:32399) — the timer is what makes it too wide
   * for the action's CTA to stay expanded beside it.
   */
  showElapsedTime?: boolean
}

/**
 * Live audio pill: waveform + elapsed open the Meeting View; pause/resume and
 * stop control the recording in place.
 */
export function SidepanelAudioPill({ showElapsedTime = true }: SidepanelAudioPillProps) {
  // Per-field selector, not useAudioNoteState(): this pill is mounted precisely
  // while the main process is rebroadcasting that whole object per transcript
  // tick, so subscribing to all of it would re-render on every line.
  const status = useAudioNoteStatus()
  const isReconnecting = status === 'reconnecting'
  const isStarting = status === 'starting'
  const isSaving = status === 'saving'
  const { isPaused, hasTranscript, togglePause, endRecording } = useLiveRecordingControls('sidepanel floating bar')
  const { openView } = useViewActions()
  const liveMeeting = useLiveMeetingIdentity()

  const handleOpenMeeting = useCallback(() => {
    openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel: 'Home' })
    AmplitudeRenderer.trackEvent('Audio Note Action', {
      action: 'open active meeting',
      source: 'sidepanel floating bar',
    })
  }, [openView, liveMeeting])

  // Bookend busy states: while starting there's no recording to control yet,
  // and while saving (Figma 8476:17270) there no longer is one — both collapse
  // the pill to a spinner and label until the status crosses into or out of
  // the live set. The same shell keeps the row's height fixed across states.
  if (isStarting || isSaving) {
    return (
      <ButtonGroup layout="shell" variant="outline" className={PILL_SHELL_CLASS}>
        <div role="status" className="flex items-center gap-1.5 px-1.5">
          <LoadingState.Spinner className="size-3.5" />
          <span className="text-foreground text-sm font-medium">{isStarting ? 'Starting notes' : 'Saving notes'}</span>
        </div>
      </ButtonGroup>
    )
  }

  return (
    <ButtonGroup layout="shell" variant="outline" className={PILL_SHELL_CLASS}>
      <Tooltip.Provider>
        <Tooltip>
          {/* Square without the timer, so the waveform centres like the other glyphs. */}
          <Tooltip.Trigger
            render={
              showElapsedTime ? (
                <Button aria-label="Open meeting" onClick={handleOpenMeeting} left={<AudioWaveform />}>
                  <AudioPillElapsed />
                </Button>
              ) : (
                <Button shape="square" aria-label="Open meeting" onClick={handleOpenMeeting}>
                  <AudioWaveform />
                </Button>
              )
            }
          />
          <Tooltip.Content>{isReconnecting ? 'Reconnecting…' : 'Open meeting'}</Tooltip.Content>
        </Tooltip>
        <MeetingMicStatus />
        <Tooltip>
          {/* A wrapper, not the button: a disabled button gets no pointer events, so the tooltip would never open. */}
          <Tooltip.Trigger render={<div className="relative flex" />}>
            <Button
              shape="square"
              aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
              onClick={togglePause}
              disabled={isReconnecting}
            >
              {isPaused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>{isPaused ? 'Resume' : 'Pause'}</Tooltip.Content>
        </Tooltip>
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button
                shape="square"
                aria-label={hasTranscript ? 'End and save recording' : 'Stop recording'}
                onClick={endRecording}
              >
                <Square aria-hidden="true" />
              </Button>
            }
          />
          <Tooltip.Content>{hasTranscript ? 'End and save' : 'Stop'}</Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
    </ButtonGroup>
  )
}
